import { useState } from "react";
import {
  Activity,
  ArrowDownUp,
  BarChart3,
  CircleAlert,
  Coins,
  ExternalLink,
  Layers3,
  SlidersHorizontal,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Field,
  IconButton,
  Select,
  SegmentedControl,
  SummaryMetrics,
} from "../../design-system";
import { ChartFrame, DataChart } from "../../design-system/patterns/charts";
import {
  initialUsageQuery,
  kstDate,
  shiftUsageDate,
  usageDimensions,
  usageMetrics,
  usageFilterLabels,
  usageFilterForDimension,
  usageQueryError,
  usageExploreFixture,
  usageExploreOptions,
  type UsageDimension,
  type UsageExploreQuery,
  type UsageExploreView,
  type UsageExploreOptions,
  type UsageExplorerExample,
  type UsageFilters,
  type UsageGroup,
  type UsageMetric,
} from "./usage-explorer-data";
import "./usage-explorer.css";

export type UsageExplorerProps = {
  /** Enable only once the host API supports the provider dimension and filter. */
  providerAvailable?: boolean;
  query: UsageExploreQuery;
  view?: UsageExploreView;
  loading?: boolean;
  error?: string;
  options?: UsageExploreOptions;
  onQueryChange: (query: UsageExploreQuery) => void;
  onRetry?: () => void;
  onOpenRun?: (group: UsageGroup) => void;
};
const number = (value: number) => value.toLocaleString("ko-KR");
const money = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
const format = (metric: UsageMetric | "failed", value: number | null) =>
  value == null
    ? "기록 없음"
    : metric === "costUsd"
      ? money(value)
      : number(value);
const palette = [
  "var(--accent)",
  "var(--success)",
  "var(--warning)",
  "var(--danger)",
  "var(--blue-line)",
  "color-mix(in srgb, var(--accent) 65%, var(--danger))",
  "color-mix(in srgb, var(--danger) 65%, var(--accent))",
  "color-mix(in srgb, var(--success) 60%, var(--accent))",
  "color-mix(in srgb, var(--warning) 60%, var(--success))",
  "color-mix(in srgb, var(--warning) 60%, var(--danger))",
];
const color = (id: string, i: number) =>
  id === "__other__" ? "var(--text-faint)" : palette[i % palette.length];
const filterKeys: (keyof UsageFilters)[] = [
  "provider",
  "customerId",
  "targetCustomerId",
  "machineId",
  "model",
  "keyId",
  "mode",
  "status",
  "jobId",
];
const tableMetrics: (UsageMetric | "failed")[] = [
  "calls",
  "inputTokens",
  "cachedTokens",
  "outputTokens",
  "totalTokens",
  "costUsd",
  "failed",
];
const tableLabels = { ...usageMetrics, failed: "실패·취소" };

/** Server totals, grouped rows and time buckets are rendered as supplied. This component never aggregates calls. */
export function UsageExplorer({
  providerAvailable = false,
  query,
  view,
  loading = false,
  error,
  options = {},
  onQueryChange,
  onRetry,
  onOpenRun,
}: UsageExplorerProps) {
  const [chartKind, setChartKind] = useState<"bar" | "line">("bar");
  const [panelOpen, setPanelOpen] = useState(false);
  const [sort, setSort] = useState<{
    key: UsageMetric | "failed";
    ascending: boolean;
  } | null>(null);
  const [notice, setNotice] = useState("");
  const invalid = usageQueryError(query);
  const change = (patch: Partial<UsageExploreQuery>) => {
    const next = { ...query, ...patch };
    if (
      next.granularity === "hour" &&
      next.from <= next.to &&
      (Date.parse(next.to) - Date.parse(next.from)) / 86400000 + 1 > 14
    ) {
      next.granularity = "day";
      setNotice("14일을 넘는 기간이므로 일 단위로 변경했습니다.");
    } else setNotice("");
    if (patch.metric) setSort(null);
    onQueryChange(next);
  };
  const range = (value: "today" | "7" | "30" | "month") => {
    const to = kstDate();
    change({
      to,
      from:
        value === "month"
          ? to.slice(0, 8) + "01"
          : shiftUsageDate(to, value === "today" ? 0 : -(Number(value) - 1)),
    });
  };
  const activeFilters = (
    Object.keys(usageFilterLabels) as (keyof UsageFilters)[]
  ).filter((key) => query[key]);
  const sorted = [...(view?.groups ?? [])].sort((a, b) => {
    const key = sort?.key ?? query.metric;
    return (sort?.ascending ? 1 : -1) * (a.total[key] - b.total[key]);
  });
  const t = view?.total;
  const filterGroup = (group: UsageGroup) => {
    const patch: Partial<UsageExploreQuery> = {};
    const ignored: string[] = [];
    for (const part of group.parts) {
      const key = usageFilterForDimension[part.dim];
      if (key && part.key) patch[key] = part.key;
      else ignored.push(usageDimensions[part.dim]);
    }
    change(patch);
    if (ignored.length)
      setNotice(
        `${ignored.join("·")} 조건은 지원하지 않아 적용하지 않았습니다. 지원되는 조건만 필터에 추가했습니다.`,
      );
    else if (group.parts.some((p) => p.dim === "customer"))
      setNotice(
        "고객사 필터는 실행·대상 고객사 양쪽에서 일치하는 기록을 포함합니다.",
      );
  };
  const totalPeriod = view?.series[0];
  const max = Math.max(
    1,
    ...(view?.chartGroups.map(
      (g) => totalPeriod?.values[g.id]?.[query.metric] ?? 0,
    ) ?? []),
  );
  const hourlyDisabled =
    !query.from ||
    !query.to ||
    query.from > query.to ||
    (Date.parse(query.to) - Date.parse(query.from)) / 86400000 + 1 > 14;
  const chartTitle =
    query.granularity === "total" ? "그룹별 사용량" : "기간별 사용량";
  return (
    <div className="usage-explorer" data-usage-explorer aria-busy={loading}>
      <div className="ue-context">
        <span>
          <span className="ue-context-dot" />
          {query.from} — {query.to}
          <small>한국 시간</small>
        </span>
        <Button
          variant="secondary"
          className="ue-panel-toggle"
          onClick={() => setPanelOpen((v) => !v)}
          aria-expanded={panelOpen}
        >
          <SlidersHorizontal size={16} />
          탐색 조건
        </Button>
      </div>
      <div className="ue-layout">
        <section className="ue-results" aria-label="사용량 탐색 결과">
          {query.dims.includes("provider") && (
            <p className="ue-notice">
              GPT 플랜은 토큰만 기록하며 호출 비용은 $0입니다. 구독료는 포함하지
              않습니다.
            </p>
          )}
          {notice && (
            <p role="status" className="ue-notice">
              {notice}
            </p>
          )}
          {activeFilters.length > 0 && (
            <div className="ue-active-filters" aria-label="적용된 사용량 필터">
              {activeFilters.map((key) => (
                <span key={key}>
                  {usageFilterLabels[key]} ·{" "}
                  {options[key]?.find((o) => o.value === query[key])?.label ??
                    query[key]}
                  <IconButton
                    label={`${usageFilterLabels[key]} 필터 해제`}
                    onClick={() => change({ [key]: undefined })}
                  >
                    <X size={13} />
                  </IconButton>
                </span>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const patch: UsageFilters = {};
                  (
                    Object.keys(usageFilterLabels) as (keyof UsageFilters)[]
                  ).forEach((key) => {
                    patch[key] = undefined;
                  });
                  change(patch);
                }}
              >
                모든 필터 해제
              </Button>
            </div>
          )}
          {invalid ? (
            <div className="ue-state" role="alert">
              <CircleAlert />
              <h3>기간을 확인해 주세요</h3>
              <p>{invalid}</p>
            </div>
          ) : loading ? (
            <div className="ue-state ue-loading" role="status">
              <span className="ue-skeleton" />
              <h3>사용량을 불러오고 있습니다</h3>
              <p>기간과 묶기 조건으로 기록을 모으는 중입니다.</p>
            </div>
          ) : error ? (
            <div className="ue-state" role="alert">
              <CircleAlert />
              <h3>사용량을 불러오지 못했습니다</h3>
              <p>{error}</p>
              {onRetry && (
                <Button variant="secondary" onClick={onRetry}>
                  다시 읽기
                </Button>
              )}
            </div>
          ) : !view || !view.total.calls ? (
            <div className="ue-state">
              <BarChart3 />
              <h3>선택한 조건에 사용량이 없습니다</h3>
              <p>기간을 넓히거나 필터를 해제해 주세요.</p>
              <Button
                variant="secondary"
                onClick={() => onQueryChange(initialUsageQuery())}
              >
                최근 7일로 초기화
              </Button>
            </div>
          ) : (
            <>
              <div className="ue-summary">
                <SummaryMetrics
                  label="사용량 합계"
                  items={[
                    {
                      label: "비용 (USD)",
                      value: money(t!.costUsd),
                      note: "기록 당시 단가 기준",
                      icon: <Coins size={18} />,
                      onSelect: () => change({ metric: "costUsd" }),
                      actionLabel: "비용으로 사용량 보기",
                    },
                    {
                      label: "총 토큰",
                      value: number(t!.totalTokens),
                      note: `입력 ${number(t!.inputTokens)} · 출력 ${number(t!.outputTokens)}`,
                      icon: <Layers3 size={18} />,
                      tone: "teal",
                      onSelect: () => change({ metric: "totalTokens" }),
                      actionLabel: "총 토큰으로 사용량 보기",
                    },
                    {
                      label: "호출 수",
                      value: number(t!.calls),
                      note: "선택한 기간 전체",
                      icon: <Activity size={18} />,
                      tone: "neutral",
                      onSelect: () => change({ metric: "calls" }),
                      actionLabel: "호출 수로 사용량 보기",
                    },
                    {
                      label: "실패·취소",
                      value: number(t!.failed),
                      note: "재시도 전 기록 포함",
                      icon: <CircleAlert size={18} />,
                      tone: t!.failed ? "amber" : "neutral",
                    },
                    {
                      label: "단가 없는 호출",
                      value: number(t!.unpriced),
                      note: t!.unpriced
                        ? "비용 합계에서 제외"
                        : "모든 성공 호출 단가 있음",
                      icon: <Coins size={18} />,
                      tone: t!.unpriced ? "amber" : "neutral",
                    },
                  ]}
                />
              </div>
              {t!.unpriced > 0 && (
                <p className="ue-warning" role="status">
                  <CircleAlert size={16} />
                  단가 없는 성공 호출 {number(t!.unpriced)}건은 비용에 포함되지
                  않습니다. 0달러 호출과 다릅니다.
                </p>
              )}
              <div className="ue-chart-top">
                <div>
                  <strong>{usageMetrics[query.metric]}</strong>
                  <span>
                    {query.dims
                      .map((dim) => usageDimensions[dim])
                      .join(" → ") || "전체 합계"}{" "}
                    · {number(view.groupCount)}개 그룹
                  </span>
                </div>
                {query.granularity !== "total" && (
                  <SegmentedControl
                    label="사용량 차트 형태"
                    value={chartKind}
                    options={[
                      { value: "bar", label: "누적 막대" },
                      { value: "line", label: "선" },
                    ]}
                    onChange={(v) => setChartKind(v as "bar" | "line")}
                  />
                )}
              </div>
              {query.granularity === "total" ? (
                <ChartFrame
                  title={chartTitle}
                  description="선택한 기간 전체 · 상위 그룹과 그 외"
                  table={
                    <div
                      className="ue-table-scroll"
                      tabIndex={0}
                      role="region"
                      aria-label="전체 사용량 차트 표"
                    >
                      <table>
                        <thead>
                          <tr>
                            <th>그룹</th>
                            <th>{usageMetrics[query.metric]}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {view.chartGroups.map((g) => (
                            <tr key={g.id}>
                              <th>{g.label}</th>
                              <td>
                                {format(
                                  query.metric,
                                  totalPeriod?.values[g.id]?.[query.metric] ??
                                    0,
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  }
                >
                  <div className="ue-horizontal">
                    {view.chartGroups.map((group, i) => {
                      const value =
                        totalPeriod?.values[group.id]?.[query.metric] ?? 0;
                      return (
                        <div
                          key={group.id}
                          className="ue-horizontal-row"
                          tabIndex={0}
                          title={`전체 · ${group.label} · ${format(query.metric, value)}`}
                        >
                          <span>
                            <i style={{ background: color(group.id, i) }} />
                            {group.label}
                          </span>
                          <div className="ue-bar-track">
                            <i
                              style={{
                                width: `${(value / max) * 100}%`,
                                background: color(group.id, i),
                              }}
                            />
                          </div>
                          <strong>{format(query.metric, value)}</strong>
                        </div>
                      );
                    })}
                  </div>
                </ChartFrame>
              ) : (
                <DataChart
                  key={query.dims.join(",") + query.granularity + query.metric}
                  title={chartTitle}
                  description={`${query.from} — ${query.to} · 한국 시간 · 범례로 그룹 표시 전환`}
                  kind={chartKind}
                  stacked={chartKind === "bar"}
                  labels={view.series.map((s) => s.period)}
                  series={view.chartGroups.map((g, i) => ({
                    name: g.label,
                    color: color(g.id, i),
                    values: view.series.map(
                      (s) => s.values[g.id]?.[query.metric] ?? 0,
                    ),
                  }))}
                  format={(v) => format(query.metric, v)}
                />
              )}
              <section
                className="ue-table-section"
                aria-label="그룹별 사용량 상세"
              >
                <header>
                  <div>
                    <h3>그룹별 상세</h3>
                    <span>
                      {number(view.groupCount)}개 그룹
                      {view.groupCount > view.groups.length &&
                        ` 중 ${number(view.groups.length)}개 표시`}
                    </span>
                  </div>
                  <Badge>
                    {tableLabels[sort?.key ?? query.metric]}{" "}
                    {sort?.ascending ? "작은 순" : "큰 순"}
                  </Badge>
                </header>
                <div
                  className="ue-table-scroll"
                  tabIndex={0}
                  role="region"
                  aria-label="그룹별 사용량 표"
                >
                  <table>
                    <thead>
                      <tr>
                        {query.dims.length ? (
                          query.dims.map((dim) => (
                            <th key={dim} scope="col">
                              {usageDimensions[dim]}
                            </th>
                          ))
                        ) : (
                          <th scope="col">그룹</th>
                        )}
                        {tableMetrics.map((key) => (
                          <th
                            key={key}
                            scope="col"
                            aria-sort={
                              (sort?.key ?? query.metric) === key
                                ? sort?.ascending
                                  ? "ascending"
                                  : "descending"
                                : "none"
                            }
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setSort({
                                  key,
                                  ascending:
                                    (sort?.key ?? query.metric) === key
                                      ? !sort?.ascending
                                      : false,
                                })
                              }
                            >
                              {tableLabels[key]}
                              <ArrowDownUp size={12} />
                            </button>
                          </th>
                        ))}
                        <th scope="col">보기</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((group) => {
                        const filterable = group.parts.some(
                          (p) => usageFilterForDimension[p.dim] && p.key,
                        );
                        const canOpen = group.parts.some(
                          (p) => (p.dim === "job" || p.dim === "run") && p.key,
                        );
                        return (
                          <tr key={group.id}>
                            {query.dims.length ? (
                              query.dims.map((dim) => (
                                <td className="ue-group-cell" key={dim}>
                                  {group.parts.find((p) => p.dim === dim)
                                    ?.label ?? "—"}
                                </td>
                              ))
                            ) : (
                              <td>전체</td>
                            )}
                            {tableMetrics.map((key) => (
                              <td key={key} className="ue-number">
                                {format(key, group.total[key])}
                              </td>
                            ))}
                            <td>
                              <div className="ue-row-actions">
                                {filterable && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`${group.label} 이것만 보기`}
                                    onClick={() => filterGroup(group)}
                                  >
                                    이것만 보기
                                  </Button>
                                )}
                                {canOpen && onOpenRun && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`${group.label} 실행 상세`}
                                    onClick={() => onOpenRun(group)}
                                  >
                                    <ExternalLink size={13} />
                                    실행 상세
                                  </Button>
                                )}
                                {!filterable && !canOpen && (
                                  <small>필터 미지원</small>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {query.dims.some(
                  (dim) => dim === "run" || dim === "device",
                ) && (
                  <p className="ue-help">
                    시도·디바이스 조건은 필터로 지원하지 않습니다. 함께 묶은
                    다른 기준만 ‘이것만 보기’에 적용됩니다.
                  </p>
                )}
                {view.groupCount > view.groups.length && (
                  <p className="ue-help">
                    표는 최대 500개 그룹을 보여 줍니다. 전체 합계와 차트는 전체
                    그룹을 포함합니다. 조건을 좁혀 나머지 그룹을 확인해 주세요.
                  </p>
                )}
              </section>
              <p className="ue-footnote">
                총 토큰은 입력 + 출력입니다. 캐시는 입력에, 추론은 출력에 이미
                포함됩니다.
                <br />
                비용은 기록 당시 단가로 계산한 추정치이며 실제 청구액과 다를 수
                있습니다.
              </p>
            </>
          )}
        </section>
        <aside
          className={`ue-controls ${panelOpen ? "is-open" : ""}`}
          aria-label="사용량 탐색 조건"
        >
          <header>
            <SlidersHorizontal size={17} />
            <strong>탐색 조건</strong>
            <span>즉시 반영</span>
          </header>
          <section>
            <h3>기간</h3>
            <div className="ue-quick" role="group" aria-label="빠른 기간">
              {(
                [
                  ["today", "오늘"],
                  ["7", "7일"],
                  ["30", "30일"],
                  ["month", "이번 달"],
                ] as const
              ).map(([value, label]) => (
                <button type="button" key={value} onClick={() => range(value)}>
                  {label}
                </button>
              ))}
            </div>
            <div className="ue-date-fields">
              <Field
                label="시작일"
                type="date"
                value={query.from}
                onChange={(e) => change({ from: e.target.value })}
              />
              <Field
                label="종료일"
                type="date"
                value={query.to}
                min={query.from}
                onChange={(e) => change({ to: e.target.value })}
              />
            </div>
            <Select
              label="기간 단위"
              value={query.granularity}
              onChange={(e) =>
                change({
                  granularity: e.target
                    .value as UsageExploreQuery["granularity"],
                })
              }
            >
              <option value="total">전체</option>
              <option value="hour" disabled={hourlyDisabled}>
                시 (14일 이내)
              </option>
              <option value="day">일</option>
              <option value="week">주</option>
              <option value="month">월</option>
            </Select>
          </section>
          <section>
            <h3>
              묶기 <small>순서대로 · 최대 3개</small>
            </h3>
            <div className="ue-dimensions" aria-label="선택된 묶기 기준">
              {query.dims.map((dim, i) => (
                <span key={dim}>
                  <b>{i + 1}</b>
                  {usageDimensions[dim]}
                  <IconButton
                    label={`${usageDimensions[dim]} 묶기 제거`}
                    onClick={() =>
                      change({ dims: query.dims.filter((d) => d !== dim) })
                    }
                  >
                    <X size={13} />
                  </IconButton>
                </span>
              ))}
            </div>
            <Select
              label="묶기 기준 추가"
              value=""
              disabled={query.dims.length >= 3}
              onChange={(e) => {
                if (e.target.value && query.dims.length < 3)
                  change({
                    dims: [...query.dims, e.target.value as UsageDimension],
                  });
              }}
            >
              <option value="">
                {query.dims.length >= 3 ? "3개 기준 선택됨" : "기준 선택"}
              </option>
              {Object.entries(usageDimensions)
                .filter(([key]) => key !== "provider" || providerAvailable)
                .filter(([key]) => !query.dims.includes(key as UsageDimension))
                .map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
            </Select>
            <Select
              label="지표"
              value={query.metric}
              onChange={(e) =>
                change({ metric: e.target.value as UsageMetric })
              }
            >
              {Object.entries(usageMetrics).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              label="차트 상위 그룹"
              value={query.top}
              onChange={(e) =>
                change({ top: Number(e.target.value) as 5 | 10 | 20 })
              }
            >
              {[5, 10, 20].map((top) => (
                <option key={top} value={top}>
                  상위 {top}개 + 그 외
                </option>
              ))}
            </Select>
          </section>
          <details className="ue-filter-details">
            <summary>
              필터
              <Badge>
                {activeFilters.length
                  ? `${activeFilters.length}개 적용`
                  : "전체"}
              </Badge>
            </summary>
            <div>
              {filterKeys
                .filter((key) => key !== "provider" || providerAvailable)
                .map((key) => (
                  <Select
                    key={key}
                    label={usageFilterLabels[key]}
                    value={query[key] ?? ""}
                    onChange={(e) =>
                      change({ [key]: e.target.value || undefined })
                    }
                  >
                    <option value="">전체</option>
                    {query[key] &&
                      !options[key]?.some((o) => o.value === query[key]) && (
                        <option value={query[key]}>{query[key]}</option>
                      )}
                    {options[key]?.map((option) => (
                      <option value={option.value} key={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                ))}
              <p className="ue-help">
                고객사 필터는 실행·대상 양쪽에 적용됩니다. 실행 고객사만 나누어
                보려면 묶기 기준을 이용하세요.
              </p>
            </div>
          </details>
        </aside>
      </div>
    </div>
  );
}

export function UsageExplorerPreview({
  example = "normal",
  onOpenRun,
}: {
  example?: UsageExplorerExample;
  onOpenRun?: UsageExplorerProps["onOpenRun"];
}) {
  const [query, setQuery] = useState<UsageExploreQuery>(() => ({
    ...initialUsageQuery(),
    ...(example === "plan"
      ? { dims: ["provider"] as UsageDimension[] }
      : example === "multi"
        ? { dims: ["job", "target"] as UsageDimension[] }
        : example === "total"
          ? { granularity: "total" as const }
          : example === "hour"
            ? { from: kstDate(), to: kstDate(), granularity: "hour" as const }
            : {}),
  }));
  const [retried, setRetried] = useState(false);
  return (
    <UsageExplorer
      providerAvailable
      query={query}
      view={usageExploreFixture(
        query,
        example === "empty",
        example === "unpriced",
        example === "plan",
      )}
      loading={example === "loading"}
      error={
        example === "error" && !retried
          ? "사용량을 읽지 못했습니다. 잠시 후 다시 시도해 주세요."
          : undefined
      }
      onQueryChange={setQuery}
      options={usageExploreOptions}
      onRetry={() => setRetried(true)}
      onOpenRun={onOpenRun}
    />
  );
}
